import {AttributeManifest, FieldConstraint} from "../models/attributeManifest.model";

// Clamp/normalize a numeric value to a constraint (from the attribute manifest —
// either a FieldConstraint for a fixed model field, or a param's Min/Max/Step).
// Integer types are rounded; each bound is optional (null/undefined = unbounded).
// Non-finite input falls back to Min (or 0). Returns the value unchanged when no
// constraint applies — so unannotated fields behave exactly as before.
export function clampValue(value: number, c?: FieldConstraint | null): number {
    if (!c) return value;

    let v = Number.isFinite(value) ? value : (c.Min ?? 0);
    if (c.Type === 'int') v = Math.round(v);
    if (c.Min != null && v < c.Min) v = c.Min;
    if (c.Max != null && v > c.Max) v = c.Max;
    return v;
}

// True when the constraint would actually change `value` — lets callers avoid a
// redundant commit (and undo entry) when the value is already in range.
export function needsClamp(value: number, c?: FieldConstraint | null): boolean {
    return !!c && clampValue(value, c) !== value;
}

// Human-readable summary of a constraint for a tooltip, e.g. "Allowed: 0 to 100",
// "Allowed: ≥ 0 · step 0.5". Undefined when there's nothing to say.
export function describeConstraint(c?: FieldConstraint | null): string | undefined {
    if (!c) return undefined;
    const parts: string[] = [];
    if (c.Min != null && c.Max != null) parts.push(`Allowed: ${c.Min} to ${c.Max}`);
    else if (c.Min != null) parts.push(`Allowed: ≥ ${c.Min}`);
    else if (c.Max != null) parts.push(`Allowed: ≤ ${c.Max}`);
    if (c.Step != null) parts.push(`step ${c.Step}`);
    return parts.length ? parts.join(' · ') : undefined;
}

// Export-time backstop: walk the (cloned) model applying every FieldConstraint,
// so shipped data is guaranteed in-range regardless of which inputs are wired or
// what was pasted/imported. Keys are data paths like "unit.properties.attack";
// the first segment selects the collection, the rest is the nested field path.
// Mutates `state` in place — pass a clone. Unknown roots are skipped.
export function applyFieldConstraints(state: any, constraints?: Record<string, FieldConstraint>): void {
    if (!state || !constraints) return;
    for (const key of Object.keys(constraints)) {
        const dot = key.indexOf('.');
        if (dot < 0) continue;
        const root = key.slice(0, dot);
        const path = key.slice(dot + 1).split('.');
        for (const obj of rootCollection(state, root))
            clampAtPath(obj, path, constraints[key]);
    }
}

// Maps a constraint key's first segment to the model objects it applies to.
// Entity roots are arrays; add singletons (e.g. 'general' → [state.globals]) here
// when those get annotated.
function rootCollection(state: any, root: string): any[] {
    switch (root) {
        case 'unit': return state.units ?? [];
        case 'building': return state.buildings ?? [];
        case 'tile': return state.tiles ?? [];
        case 'resource': return state.resources ?? [];
        // The RemoteConstants root itself (General-tab globals + noise maps).
        case 'general': return [state];
        // Every level of every Unit Upgrade track (ConstraintRootKeys.UNIT_UPGRADE_LEVEL).
        case 'upgradelevel': return (state.unitUpgrades ?? []).flatMap((t: any) => t.levels ?? []);
        default: return [];
    }
}

function clampAtPath(obj: any, path: string[], c: FieldConstraint): void {
    let o = obj;
    for (let i = 0; i < path.length - 1; i++) {
        if (o == null) return;
        o = o[path[i]];
    }
    if (o == null) return;
    const leaf = path[path.length - 1];
    if (typeof o[leaf] === 'number') o[leaf] = clampValue(o[leaf], c);
}

// Export-time backstop for attribute-parameter values, which live in each
// metadata's `values` dict (not on a model field). For every metadata in the
// attribute collections, find its manifest entry by id and clamp each authored
// value to its param's range. Catches values that bypassed the input clamp
// (paste / import / branch-migrate). Mutates `state` in place — pass a clone.
export function applyParamConstraints(state: any, manifest?: AttributeManifest): void {
    if (!state || !manifest) return;
    const pairs: [any[] | undefined, any[] | undefined][] = [
        [state.unitAttributes, manifest.UnitAttributes],
        [state.buildingAttributes, manifest.BuildingAttributes],
        [state.rewards, manifest.CityUpgrades],
        [state.techPassiveAttributes, manifest.TechPassives],
        [state.techActionAttributes, manifest.TechActions],
    ];
    for (const [metas, entries] of pairs) {
        if (!metas || !entries) continue;
        for (const meta of metas) {
            const entry = entries.find(e => e?.Ids?.includes(meta.id));
            if (!entry?.Parameters || !meta.values) continue;
            for (const p of entry.Parameters) {
                if (p.Min == null && p.Max == null && p.Step == null) continue;
                if (typeof meta.values[p.Name] === 'number')
                    meta.values[p.Name] = clampValue(meta.values[p.Name], p);
            }
        }
    }
}
